/**
 * Create Task Dialog
 *
 * Modal form for creating new agent tasks.
 *
 * @module features/tasks/components/CreateTaskDialog
 */

import React from 'react';
import { Dialog, DialogTrigger, DialogContent, DialogFooter, Button, TextField, TextArea, Select, useToast } from '@chatsdet/ui';
import { Plus } from '@phosphor-icons/react';
import { useCreateTask } from '../hooks/useTasks';
import { useTaskRepos } from '../hooks/useTaskRepos';
import type { AgentTaskTriggerType } from '../types';

const TRIGGER_OPTIONS = [
  { id: 'production-deploy', label: 'Production Deploy' },
  { id: 'flag-deprecated', label: 'Flag Deprecated' },
  { id: 'flag-cleanup', label: 'Flag Cleanup' },
  { id: 'knob-deprecated', label: 'Knob Deprecated' },
  { id: 'knob-cleanup', label: 'Knob Cleanup' },
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'pr-merged', label: 'PR Merged' },
  { id: 'immediate', label: 'Immediate' },
  { id: 'manual', label: 'Manual' },
];

const PRIORITY_OPTIONS = [
  { id: '0', label: 'Normal' },
  { id: '1', label: 'High' },
  { id: '2', label: 'Critical' },
];

export function CreateTaskDialog() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [repoId, setRepoId] = React.useState<string>('');
  const [triggerType, setTriggerType] = React.useState<string>('immediate');
  const [priority, setPriority] = React.useState<string>('0');
  const [tags, setTags] = React.useState('');

  const { data: reposData } = useTaskRepos();
  const createTask = useCreateTask();
  const { toast } = useToast();

  const repoOptions = React.useMemo(
    () =>
      (reposData?.data ?? []).map((r) => ({
        id: r.id,
        label: r.repoFullName,
        description: r.propertyName,
      })),
    [reposData]
  );

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setRepoId('');
    setTriggerType('immediate');
    setPriority('0');
    setTags('');
  };

  const handleSubmit = () => {
    if (!repoId || !title.trim() || !description.trim()) return;

    createTask.mutate(
      {
        propertyGithubRepoId: repoId,
        title: title.trim(),
        description: description.trim(),
        triggerType: triggerType as AgentTaskTriggerType,
        priority: parseInt(priority, 10),
        tags: tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
      },
      {
        onSuccess: () => {
          toast.success('Task created');
          resetForm();
          setIsOpen(false);
        },
        onError: () => {
          toast.error('Failed to create task');
        },
      }
    );
  };

  const canSubmit = !!repoId && !!title.trim() && !!description.trim() && !createTask.isPending;

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen} size="lg">
      <Button variant="primary" size="sm" iconLeft={<Plus weight="bold" size={16} />}>
        New Task
      </Button>
      <Dialog title="Create Task" description="Create a new agent task for follow-up work.">
        <DialogContent>
          <Select
            label="Repository"
            placeholder="Select a repository"
            items={repoOptions}
            selectedKey={repoId}
            onSelectionChange={(key) => setRepoId(key as string)}
          />
          <TextField
            label="Title"
            value={title}
            onChange={setTitle}
            placeholder="Brief description of the task"
          />
          <TextArea
            label="Description"
            value={description}
            onChange={setDescription}
            placeholder="What needs to be done and where in the codebase"
            className="min-h-[100px]"
          />
          <Select
            label="Trigger Type"
            items={TRIGGER_OPTIONS}
            selectedKey={triggerType}
            onSelectionChange={(key) => setTriggerType(key as string)}
          />
          <Select
            label="Priority"
            items={PRIORITY_OPTIONS}
            selectedKey={priority}
            onSelectionChange={(key) => setPriority(key as string)}
          />
          <TextField
            label="Tags"
            value={tags}
            onChange={setTags}
            placeholder="Comma-separated tags (e.g. cleanup, feature-flags)"
          />
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onPress={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit}
          >
            {createTask.isPending ? 'Creating...' : 'Create Task'}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
